import type { Venue } from '../types';
import { type Origin, distanceMiles } from './eventFilters';
import { getVenueCategories } from './venue-categories';

export type VenueSortKey = 'name-asc' | 'name-desc' | 'distance-asc' | 'neighborhood-asc';
export type VenueActiveFilter = 'all' | 'active' | 'inactive';
export type VenueSocialFilter = 'all' | 'ig' | 'fb' | 'both' | 'any' | 'none';
export type VenueConfidenceFilter = 'all' | 'high' | 'medium' | 'low' | 'closed';
export type VenueScanFreshness = 'all' | 'never' | 'stale' | 'recent';
/**
 * Admin-only review-status filter. Distinguishes the reasons a venue
 * can be in a non-default state:
 *   - `pending`: auto-discovered but waiting for admin approval
 *     (`reviewStatus === 'pending'`).
 *   - `declined`: admin explicitly declined a new venue
 *     (`reviewStatus === 'declined'`). Hides the venue AND its events.
 *   - `dismissed`: admin removed the venue from the active venues list
 *     but wants its events to keep showing
 *     (`reviewStatus === 'dismissed'`).
 *   - `dismissed-legacy`: admin manually deactivated an old auto-approved
 *     venue back when there was no review gate (`active === false` AND no
 *     `reviewStatus`).
 *   - `approved`: real, approved venue (`reviewStatus === 'approved'` OR
 *     legacy active row with no `reviewStatus`).
 */
export type VenueReviewStatusFilter =
  | 'all'
  | 'pending'
  | 'declined'
  | 'dismissed'
  | 'dismissed-legacy'
  | 'approved'
  | 'auto-declined';

const UNCATEGORIZED = '__uncategorized__';
export const VENUE_UNCATEGORIZED = UNCATEGORIZED;

/**
 * Filter state shared across the public Browse, public Map, and admin Manage
 * venue pages. Public pages typically only mutate `search`, `neighborhoods`,
 * `categories`, `social`, `origin`, `maxDistanceMiles`, and `sort`; admin
 * pages additionally exercise `active`, `confidence`, and `scanFreshness`.
 */
export interface VenueFilterState {
  search: string;
  neighborhoods: Set<string>;
  categories: Set<string>;
  social: VenueSocialFilter;
  active: VenueActiveFilter;
  confidence: VenueConfidenceFilter;
  scanFreshness: VenueScanFreshness;
  /** Admin-only. Defaults to `'all'`; public pages ignore this. */
  reviewStatus: VenueReviewStatusFilter;
  origin?: Origin;
  maxDistanceMiles?: number;
  sort: VenueSortKey;
}

export const DEFAULT_VENUE_FILTERS: VenueFilterState = {
  search: '',
  neighborhoods: new Set(),
  categories: new Set(),
  social: 'all',
  active: 'all',
  confidence: 'all',
  scanFreshness: 'all',
  reviewStatus: 'all',
  origin: undefined,
  maxDistanceMiles: undefined,
  sort: 'name-asc',
};

/** Returns the review-status bucket a venue falls into. Pure function so it
 *  can be reused by KPI counters, filtering, and per-row badges. */
export function venueReviewBucket(v: Venue): 'pending' | 'declined' | 'dismissed' | 'dismissed-legacy' | 'approved' | 'auto-declined' {
  if (v.autoDeclined === true && v.reviewStatus === 'declined') return 'auto-declined';
  if (v.reviewStatus === 'pending') return 'pending';
  if (v.reviewStatus === 'declined') return 'declined';
  if (v.reviewStatus === 'dismissed') return 'dismissed';
  if (v.active === false && !v.reviewStatus) return 'dismissed-legacy';
  return 'approved';
}

/** Public-page default differs from admin-page default. Admin pages typically
 *  hide deactivated venues; public pages should never see them anyway because
 *  the API filters them upstream, but this keeps filter sets symmetric. */
export const DEFAULT_ADMIN_VENUE_FILTERS: VenueFilterState = {
  ...DEFAULT_VENUE_FILTERS,
  active: 'active',
};

/** Convert a Venue into a "scan freshness" bucket used both for filtering and
 *  for the admin grouping. Mirrors the legacy AdminVenuesPage helper. */
export function scanFreshnessOf(v: Venue): 'never' | 'stale' | 'recent' {
  if (!v.lastSocialScanAt) return 'never';
  const days = (Date.now() - new Date(v.lastSocialScanAt).getTime()) / 86_400_000;
  if (days > 14) return 'stale';
  return 'recent';
}

export function venueConfidenceBucket(score: number | undefined): 'high' | 'medium' | 'low' | 'unknown' {
  if (typeof score !== 'number') return 'unknown';
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
}

export function isVenueClosed(v: Venue): boolean {
  return v.businessStatus === 'CLOSED_PERMANENTLY' || v.businessStatus === 'CLOSED_TEMPORARILY';
}

export function venueSocialBucket(v: Venue): 'both' | 'ig' | 'fb' | 'none' {
  const ig = !!v.instagramHandle;
  const fb = !!v.facebookUrl;
  if (ig && fb) return 'both';
  if (ig) return 'ig';
  if (fb) return 'fb';
  return 'none';
}

export function venueCategoryKeys(v: Venue): string[] {
  const cats = getVenueCategories(v);
  return cats.length ? cats : [UNCATEGORIZED];
}

/** Apply every filter except sort. Returns a new array. */
export function filterVenues(venues: Venue[], f: VenueFilterState): Venue[] {
  const q = f.search.trim().toLowerCase();
  return venues.filter((v) => {
    if (f.active === 'active' && v.active === false) return false;
    if (f.active === 'inactive' && v.active !== false) return false;

    if (f.neighborhoods.size > 0 && !f.neighborhoods.has(v.partitionKey)) return false;

    if (f.categories.size > 0) {
      const cats = getVenueCategories(v);
      if (f.categories.has(UNCATEGORIZED)) {
        if (cats.length > 0 && !cats.some((c) => f.categories.has(c))) return false;
      } else if (!cats.some((c) => f.categories.has(c))) {
        return false;
      }
    }

    if (f.social !== 'all') {
      const ig = !!v.instagramHandle;
      const fb = !!v.facebookUrl;
      if (f.social === 'ig' && !ig) return false;
      if (f.social === 'fb' && !fb) return false;
      if (f.social === 'both' && !(ig && fb)) return false;
      if (f.social === 'any' && !(ig || fb)) return false;
      if (f.social === 'none' && (ig || fb)) return false;
    }

    if (f.confidence !== 'all') {
      if (f.confidence === 'closed') {
        if (!isVenueClosed(v)) return false;
      } else if (venueConfidenceBucket(v.venueConfidence) !== f.confidence) {
        return false;
      }
    }

    if (f.scanFreshness !== 'all' && scanFreshnessOf(v) !== f.scanFreshness) return false;

    if (f.reviewStatus !== 'all' && venueReviewBucket(v) !== f.reviewStatus) return false;

    if (f.origin && typeof f.maxDistanceMiles === 'number' && f.maxDistanceMiles > 0) {
      const d = distanceMiles(f.origin.lat, f.origin.lng, v.lat, v.lng);
      if (!Number.isFinite(d) || d > f.maxDistanceMiles) return false;
    }

    if (q) {
      const hay = [
        v.name,
        v.partitionKey,
        ...getVenueCategories(v),
        v.address,
        v.instagramHandle,
        v.facebookUrl,
        v.website,
        v.rowKey,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }

    return true;
  });
}

/** Stable sort applied after filtering. Pure — does not mutate input. */
export function sortVenues(venues: Venue[], sort: VenueSortKey, origin?: Origin): Venue[] {
  const out = [...venues];
  switch (sort) {
    case 'name-asc':
      out.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'name-desc':
      out.sort((a, b) => b.name.localeCompare(a.name));
      break;
    case 'neighborhood-asc':
      out.sort((a, b) =>
        (a.partitionKey || '').localeCompare(b.partitionKey || '') ||
        a.name.localeCompare(b.name),
      );
      break;
    case 'distance-asc': {
      if (!origin) {
        out.sort((a, b) => a.name.localeCompare(b.name));
        break;
      }
      out.sort((a, b) => {
        const da = distanceMiles(origin.lat, origin.lng, a.lat, a.lng);
        const db = distanceMiles(origin.lat, origin.lng, b.lat, b.lng);
        return da - db;
      });
      break;
    }
  }
  return out;
}

/** Convenience: filter + sort in one call. */
export function applyVenueFilters(venues: Venue[], f: VenueFilterState): Venue[] {
  return sortVenues(filterVenues(venues, f), f.sort, f.origin);
}

/** Count "active" filter chips for the toolbar badge. Origin/distance are not
 *  counted because the DistanceControl pill shows its own state. */
export function countActiveVenueFilters(f: VenueFilterState): number {
  let n = 0;
  if (f.search.trim()) n += 1;
  if (f.neighborhoods.size) n += 1;
  if (f.categories.size) n += 1;
  if (f.social !== 'all') n += 1;
  if (f.active !== 'all') n += 1;
  if (f.confidence !== 'all') n += 1;
  if (f.scanFreshness !== 'all') n += 1;
  if (f.reviewStatus !== 'all') n += 1;
  return n;
}

// ---------- URL serialization ----------

const SET_KEYS = ['neighborhoods', 'categories'] as const;

/** Read filter state from URLSearchParams. Unknown keys are ignored; missing
 *  values fall back to defaults. */
export function venueFiltersFromParams(
  params: URLSearchParams,
  defaults: VenueFilterState = DEFAULT_VENUE_FILTERS,
): VenueFilterState {
  const out: VenueFilterState = {
    ...defaults,
    neighborhoods: new Set(defaults.neighborhoods),
    categories: new Set(defaults.categories),
  };
  const q = params.get('q');
  if (q !== null) out.search = q;
  for (const key of SET_KEYS) {
    const raw = params.get(setParamKey(key));
    if (raw !== null) out[key] = new Set(raw.split(',').filter(Boolean));
  }
  const social = params.get('social');
  if (social && isValidSocial(social)) out.social = social;
  const active = params.get('active');
  if (active && isValidActive(active)) out.active = active;
  const conf = params.get('conf');
  if (conf && isValidConfidence(conf)) out.confidence = conf;
  const scan = params.get('scan');
  if (scan && isValidScan(scan)) out.scanFreshness = scan;
  const review = params.get('review');
  if (review && isValidReview(review)) out.reviewStatus = review;
  const sort = params.get('sort');
  if (sort && isValidSort(sort)) out.sort = sort;
  return out;
}

/** Serialize the parts of the filter state that should appear in the URL.
 *  `origin`/`maxDistanceMiles` are owned by the page (often via
 *  {@link useStoredOrigin}) and serialised separately. */
export function venueFiltersToParams(
  f: VenueFilterState,
  defaults: VenueFilterState = DEFAULT_VENUE_FILTERS,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  out.q = f.search.trim() || undefined;
  for (const key of SET_KEYS) {
    out[setParamKey(key)] = f[key].size ? Array.from(f[key]).sort().join(',') : undefined;
  }
  out.social = f.social !== defaults.social ? f.social : undefined;
  out.active = f.active !== defaults.active ? f.active : undefined;
  out.conf = f.confidence !== defaults.confidence ? f.confidence : undefined;
  out.scan = f.scanFreshness !== defaults.scanFreshness ? f.scanFreshness : undefined;
  out.review = f.reviewStatus !== defaults.reviewStatus ? f.reviewStatus : undefined;
  out.sort = f.sort !== defaults.sort ? f.sort : undefined;
  return out;
}

function setParamKey(key: 'neighborhoods' | 'categories'): string {
  return key === 'neighborhoods' ? 'nb' : 'cat';
}

function isValidSocial(v: string): v is VenueSocialFilter {
  return ['all', 'ig', 'fb', 'both', 'any', 'none'].includes(v);
}
function isValidActive(v: string): v is VenueActiveFilter {
  return ['all', 'active', 'inactive'].includes(v);
}
function isValidConfidence(v: string): v is VenueConfidenceFilter {
  return ['all', 'high', 'medium', 'low', 'closed'].includes(v);
}
function isValidScan(v: string): v is VenueScanFreshness {
  return ['all', 'never', 'stale', 'recent'].includes(v);
}
function isValidReview(v: string): v is VenueReviewStatusFilter {
  return ['all', 'pending', 'declined', 'dismissed-legacy', 'approved', 'auto-declined'].includes(v);
}
function isValidSort(v: string): v is VenueSortKey {
  return ['name-asc', 'name-desc', 'distance-asc', 'neighborhood-asc'].includes(v);
}
